import { useCallback, useEffect, useMemo, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chip } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useGetAuditLogByIdQuery, useTenantDetails } from '@circuitry-ai/doc-data';
import { convertDateFormat } from '../utils/commonutils';
import { checkType, CtyShowToast } from '../toast/CtyToast';
import { getAuditStatusChipClass, getAuditTypeChipClass } from './auditLogsUtils';
import { AuditLogDetailBody } from './auditLogDetailViews';
import CtySpinner from '../spinner/CtySpinner';

const DEFAULT_DATE_TIME_FORMAT = 'MM-dd-yyyy HH:mm:ss';

export interface AuditLogDetailDrawerProps {
  isOpen: boolean;
  auditId: string | null;
  onClose: () => void;
  dateTimeFormat?: string;
}

function formatRelativeTime(value: string): string {
  const date = new Date(value.includes('T') ? value : value.replace(' ', 'T') + 'Z');
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function AuditLogDetailDrawer({
  isOpen,
  auditId,
  onClose,
  dateTimeFormat,
}: Readonly<AuditLogDetailDrawerProps>) {
  const { t } = useTranslation();
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const { tenantDetails } = useTenantDetails();

  const { data: detail, isLoading, isFetching, error } = useGetAuditLogByIdQuery(auditId ?? '', {
    skip: !isOpen || !auditId,
    refetchOnMountOrArgChange: true,
  });

  const isDetailReady = detail?.audit_id === auditId;
  const isDetailLoading = Boolean(
    isOpen && auditId && (isLoading || isFetching || isDetailReady === false)
  );

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const handleBackdropClose = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onClose();
    },
    [onClose]
  );

  const resolvedFormat = dateTimeFormat || DEFAULT_DATE_TIME_FORMAT;

  const moduleTitle = useMemo(() => {
    if (!detail) return '';
    return [detail.module, detail.sub_module].filter(Boolean).join(' / ');
  }, [detail?.module, detail?.sub_module]);

  const formattedWhen = useMemo(() => {
    if (!detail?.executed_at) return '--';
    const formatted = convertDateFormat(detail.executed_at, resolvedFormat) || detail.executed_at;
    const relative = formatRelativeTime(detail.executed_at);
    if (relative) {
      return formatted + ' (' + relative + ')';
    }
    return formatted;
  }, [detail?.executed_at, resolvedFormat]);

  const tenantDisplay = detail?.tenant_id || tenantDetails?.org_name || '--';

  const handleCopy = useCallback(async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      CtyShowToast(checkType.success, t('Copied to clipboard'));
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      CtyShowToast(checkType.error, t('Failed to copy'));
    }
  }, [t]);

  if (!auditId) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="audit-detail-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, pointerEvents: 'auto' }}
            exit={{ opacity: 0, pointerEvents: 'none' }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[200] bg-black/40"
            onMouseDown={handleBackdropClose}
            aria-hidden
          />
          <motion.aside
            key="audit-detail-drawer"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 260 }}
            className="fixed right-0 top-0 z-[201] flex h-full w-full max-w-[620px] flex-col border-l border-divider bg-background shadow-2xl"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 border-b border-divider px-6 py-4">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  {detail?.audit_type && (
                    <Chip
                      size="sm"
                      variant="flat"
                      classNames={{
                        base: `border capitalize ${getAuditTypeChipClass(detail.audit_type)}`,
                        content: 'capitalize text-xs font-medium',
                      }}
                    >
                      {t(detail.audit_type)}
                    </Chip>
                  )}
                  <h2 className="truncate text-xl font-bold">{moduleTitle || t('Audit log details')}</h2>
                  {detail?.status && (
                    <Chip
                      size="sm"
                      variant="flat"
                      classNames={{
                        base: `border capitalize ${getAuditStatusChipClass(detail.status)}`,
                        content: 'capitalize text-xs font-medium',
                      }}
                    >
                      {t(detail.status)}
                    </Chip>
                  )}
                </div>
                {detail?.description && (
                  <p className="mt-2 text-sm text-default-500">{detail.description}</p>
                )}
              </div>
              <Button isIconOnly size="sm" variant="light" onPress={onClose} aria-label={t('Close')}>
                <Icon icon="mdi:close" className="h-5 w-5" />
              </Button>
            </div>

            <div className="relative flex-1 overflow-y-auto px-6 py-4">
              {isDetailLoading && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-background/90">
                  <CtySpinner size="lg" />
                  <p className="text-sm text-default-500">{t('Loading audit log details...')}</p>
                </div>
              )}

              {isDetailLoading ? null : error ? (
                <p className="py-8 text-center text-danger">{t('Failed to load audit log details')}</p>
              ) : null}

              {isDetailReady && detail && (
                <AuditLogDetailBody
                  detail={detail}
                  tenantDisplay={tenantDisplay}
                  formattedWhen={formattedWhen}
                  moduleTitle={moduleTitle}
                  copiedField={copiedField}
                  onCopy={handleCopy}
                />
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

export { AuditLogDetailDrawer };
